import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import {
  useListBrokerageFiles,
  BrokerageFile,
} from "@/lib/api/brokerage";
import { CommonDestinationTimezone } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FilterBar } from "./components/FilterBar";
import { BrokerageFilesTable } from "./tables/BrokerageFilesTable";
import { useQueryParam } from "@/hooks/use-query-param";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BrokerageDetails } from "./Details";
import { Loader } from "lucide-react";

export interface DateRange {
  from: string;
  to: string;
}

export function BrokerageDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, onTabChange] = useState<"files" | "details">('files');

  return (
    <div>
      <Tabs value={currentTab} onValueChange={(tabValue: string) => onTabChange(tabValue as any)} className="h-full flex flex-col">
          <TabsList className="grid grid-cols-5 w-[750px] mb-4">
            <TabsTrigger value="files">Files</TabsTrigger>
                  {/* <TabsTrigger value="details">Allocations</TabsTrigger> */}
          </TabsList>

          <TabsContent value="files" className="flex-1 overflow-hidden mt-0">
            <FilesDashboard />
          </TabsContent>
              {/*
          <TabsContent value="details" className="flex-1 overflow-hidden mt-0">
            <BrokerageDetails />
          </TabsContent> */}
      </Tabs>
    </div>

  )
}


function FilesDashboard() {
  const [, setSearchParams] = useSearchParams();
  
  // Default to last 14 days
  const today = moment.tz(CommonDestinationTimezone);
  const twoWeeksAgo = today.clone().subtract(4, "days");
  const defaultFrom = twoWeeksAgo.format("YYYY-MM-DD");
  const defaultTo = today.format("YYYY-MM-DD");
  
  // Use individual query params for dates
  const [fromDate, setFromDate] = useQueryParam("from", defaultFrom);
  const [toDate, setToDate] = useQueryParam("to", defaultTo);
  
  const [page, setPage] = useState(1);
  const limit = 1000;
  const [showDormanOnly, setShowDormanOnly] = useState(true);
  
  // Fetch brokerage files
  const filesQuery = useListBrokerageFiles({
    page,
    limit,
    request_date_from: fromDate,
    request_date_to: toDate,
  });
  
  const filesResult = filesQuery;
  const isLoading = filesQuery.isLoading;
  const error = filesQuery.error;
  const hasError = error || (filesResult?.data && !filesResult?.data.success);
  
  const rawFiles = filesResult?.data?.data?.data || [];
  
  // Add provider_type to files since API endpoint is for Dorman
  const allFiles = rawFiles;
  
  // Filter files based on showDormanOnly toggle
  const files = showDormanOnly 
    ? allFiles.filter((file: BrokerageFile) => file.provider_type === 'dorman')
    : allFiles.filter((file: BrokerageFile) => file.provider_type !== 'dorman' && !/disclaimer/im.test(file.name || file.Document?.filename || ""));

  const pagination = filesResult?.data?.data?.pagination;
  
  const handleDateRangeChange = (newRange: DateRange) => {
    // When changing both dates, use setSearchParams for batch update
    if (newRange.from !== fromDate && newRange.to !== toDate) {
      const params = new URLSearchParams();
      params.set("from", newRange.from);
      params.set("to", newRange.to);
      setSearchParams(params);
    } else {
      // Otherwise use individual setters
      if (newRange.from !== fromDate) setFromDate(newRange.from);
      if (newRange.to !== toDate) setToDate(newRange.to);
    }
    setPage(1); // Reset to first page when filtering
  };
  
  const dateRange: DateRange = {
    from: fromDate,
    to: toDate,
  };
  
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-bold flex flex-row items-center gap-2">Brokerage Statements{isLoading && (<><Loader className="h-4 w-4 animate-spin" /><span className="text-xs text-muted-foreground">Loading</span></>)}</h1>
        <p className="text-muted-foreground mt-2">
          View and download brokerage daily statement files
        </p>
      </div>
      
      <FilterBar
        presetType="files"
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        showDormanOnly={showDormanOnly}
        onShowDormanOnlyChange={setShowDormanOnly}
      />
      
      {hasError && (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load brokerage files. Please try again later.
          </AlertDescription>
        </Alert>
      )}
      
      <BrokerageFilesTable
        isLoading={isLoading}
        files={files || []}
        pagination={pagination}
        currentPage={page}
        onPageChange={setPage}
      />
    </div>
  );
}
